// ForgeTrack — Global Work Items View
// Route: /work-items

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, { GlassPanel, EmptyState, LoadingShimmer, StatusBadge } from '../components/engine/EnginePageShell';
import { useDebounce } from '../components/engine/hooks';
import { useWorkItems, useProjectList, type WorkItemSearchResult } from '../lib/api/hooks-extended';

export const Route = createFileRoute('/work-items')({
  component: WorkItemsPage,
});

function WorkItemsPage() {
  const { data: projects } = useProjectList();
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState<number | undefined>(undefined);
  const debouncedSearch = useDebounce(search, 300);

  const { data: items, loading, error } = useWorkItems({
    search: debouncedSearch || undefined,
    projectId: projectFilter,
  });

  const statusColor = (s: string) =>
    s === 'Completed' ? C.neon : s === 'InProgress' ? C.cyan : s === 'Blocked' ? C.coral : C.amber;

  return (
    <EnginePageShell title="Work Items" icon="📋" accentColor={C.cyan}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search work items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '8px 14px', borderRadius: 8, fontSize: 13,
            background: C.panel, color: C.text, border: `1px solid ${C.faint}`,
            fontFamily: F.display, outline: 'none',
          }}
        />
        <select
          value={projectFilter ?? ''}
          onChange={(e) => setProjectFilter(e.target.value ? Number(e.target.value) : undefined)}
          style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 13,
            background: C.panel, color: C.text, border: `1px solid ${C.faint}`,
            fontFamily: F.display, outline: 'none', minWidth: 200,
          }}
        >
          <option value="">All Projects</option>
          {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading && <LoadingShimmer />}

      {error && !loading && (
        <EmptyState icon="⚠️" title="Could not load work items" subtitle={typeof error === 'object' && error !== null && 'message' in error ? String((error as any).message) : 'An error occurred'} />
      )}

      {!loading && !error && items && items.length === 0 && (
        <EmptyState icon="📋" title="No work items found" subtitle="Try adjusting your search or project filter." />
      )}

      {!loading && !error && items && items.length > 0 && (
        <GlassPanel accent={C.faint}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: F.display }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.faint}` }}>
                  {['Title', 'Project', 'Phase', 'Status', 'Complexity', 'Effort (wks)'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item: WorkItemSearchResult) => (
                  <tr key={item.id} style={{ borderBottom: `1px solid ${C.faint}08` }}>
                    <td style={{ padding: '10px 14px' }}>
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: String(item.projectId) }}
                        style={{ color: C.cyan, textDecoration: 'none', fontWeight: 500 }}
                      >
                        {item.title}
                      </Link>
                    </td>
                    <td style={{ padding: '10px 14px', color: C.textSoft }}>{item.projectName}</td>
                    <td style={{ padding: '10px 14px', color: C.dim }}>{item.phaseName ?? '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <StatusBadge color={statusColor(item.status)} label={item.status} />
                    </td>
                    <td style={{ padding: '10px 14px', color: C.violet, fontFamily: F.mono }}>
                      {item.complexityScore ?? '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: C.amber, fontFamily: F.mono }}>
                      {item.equivalentWeeks?.toFixed(1) ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassPanel>
      )}
    </EnginePageShell>
  );
}
